// SCR-CONSOLE-021 — ماتریس انطباق (US-INT-043؛ AP-INT-02): وضعیت مستندات، محیط تست، مجوز، آخرین تغییر API و مسئول هر سامانه
// حاکمیتی (G-01..G-09)؛ ویرایش با rowVersion (تعارض ← PHX-GEN-0003) و ثبت Audit در سرور.
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { unwrap, type ComplianceItemView } from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toNumber } from '../../api';
import { intConsoleKeys } from './IntegrationsPage';

export const SCR_CONSOLE_021 = 'SCR-CONSOLE-021';
const STATUSES = ['Designed', 'AwaitingDocumentation', 'AwaitingLicense', 'Implemented', 'Approved'] as const;
const ACCESS = ['NotRequested', 'Requested', 'Granted', 'Rejected', 'NotApplicable'] as const;

export function CompliancePage() {
  const { t } = useTranslation('int');
  const { api } = usePlatformSession();
  const [editing, setEditing] = useState<ComplianceItemView | null>(null);
  const matrix = useQuery({
    queryKey: intConsoleKeys.compliance,
    queryFn: () => unwrap(api.GET('/api/v1/integration/compliance')),
  });

  const columns: DataTableColumn<ComplianceItemView>[] = [
    { id: 'code', headerKey: 'int:compliance.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'system', headerKey: 'int:compliance.columns.system', cell: (row) => row.title },
    { id: 'authority', headerKey: 'int:compliance.columns.authority', cell: (row) => row.authority },
    { id: 'phase', headerKey: 'int:compliance.columns.phase', cell: (row) => faDigits(toNumber(row.phase) ?? 0) },
    {
      id: 'status',
      headerKey: 'int:compliance.columns.status',
      cell: (row) => (
        <span data-testid={`compliance-status-${row.code}`} data-status={row.status}>
          {t(`int:compliance.status.${row.status}`, { defaultValue: row.status })}
        </span>
      ),
    },
    { id: 'docs', headerKey: 'int:compliance.columns.docs', cell: (row) => row.docsVersion ?? '—' },
    {
      id: 'test_env',
      headerKey: 'int:compliance.columns.test_env',
      cell: (row) => t(`int:compliance.access.${row.testEnvironmentStatus}`, { defaultValue: row.testEnvironmentStatus }),
    },
    {
      id: 'license',
      headerKey: 'int:compliance.columns.license',
      cell: (row) => t(`int:compliance.access.${row.licenseStatus}`, { defaultValue: row.licenseStatus }),
    },
    {
      id: 'changed',
      headerKey: 'int:compliance.columns.changed',
      cell: (row) => (row.lastApiChangeOn ? <JalaliDate value={row.lastApiChangeOn} /> : '—'),
    },
    { id: 'owner', headerKey: 'int:compliance.columns.owner', cell: (row) => row.owner ?? '—' },
    {
      id: 'actions',
      headerKey: 'int:compliance.columns.actions',
      cell: (row) => (
        <Tooltip title={t('int:compliance.edit')}>
          <IconButton
            aria-label={`${t('int:compliance.edit')} ${row.code}`}
            onClick={() => setEditing(row)}
            data-testid={`compliance-edit-${row.code}`}
          >
            <EditOutlinedIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <ListPage screenId={SCR_CONSOLE_021} titleKey="int:compliance.title" subtitleKey="int:compliance.subtitle">
      <DataTable
        captionKey="int:compliance.caption"
        columns={columns}
        rows={matrix.data?.items}
        getRowId={(row) => row.code}
        isLoading={matrix.isPending}
        error={matrix.error}
        onRetry={() => void matrix.refetch()}
      />
      {editing ? <ComplianceDialog item={editing} onClose={() => setEditing(null)} /> : null}
    </ListPage>
  );
}

interface ComplianceForm {
  status: string;
  docsVersion: string;
  testEnvironmentStatus: string;
  licenseStatus: string;
  lastApiChangeOn: string | null;
  owner: string;
  risks: string;
}

function ComplianceDialog({ item, onClose }: { item: ComplianceItemView; onClose: () => void }) {
  const { t } = useTranslation('int');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const { control, handleSubmit, formState } = useForm<ComplianceForm>({
    defaultValues: {
      status: item.status,
      docsVersion: item.docsVersion ?? '',
      testEnvironmentStatus: item.testEnvironmentStatus,
      licenseStatus: item.licenseStatus,
      lastApiChangeOn: item.lastApiChangeOn,
      owner: item.owner ?? '',
      risks: item.risks ?? '',
    },
  });
  const save = useMutation({
    mutationFn: (form: ComplianceForm) =>
      unwrap(
        api.PUT('/api/v1/integration/compliance/{code}', {
          params: { path: { code: item.code } },
          body: {
            status: form.status,
            docsVersion: form.docsVersion.trim() || null,
            testEnvironmentStatus: form.testEnvironmentStatus,
            licenseStatus: form.licenseStatus,
            lastApiChangeOn: form.lastApiChangeOn,
            owner: form.owner.trim() || null,
            risks: form.risks.trim() || null,
            rowVersion: item.rowVersion,
          },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intConsoleKeys.compliance });
      onClose();
    },
  });

  const select = (name: 'status' | 'testEnvironmentStatus' | 'licenseStatus', labelKey: string, values: readonly string[], prefix: string) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          select
          label={t(labelKey)}
          {...field}
          slotProps={{ htmlInput: { 'data-testid': `compliance-${name}` } }}
        >
          {values.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`${prefix}.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
  const text = (name: 'docsVersion' | 'owner' | 'risks', labelKey: string, multiline = false) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          label={t(labelKey)}
          multiline={multiline}
          minRows={multiline ? 2 : undefined}
          {...field}
          slotProps={{ htmlInput: { 'data-testid': `compliance-${name}`, maxLength: multiline ? 2000 : 200 } }}
        />
      )}
    />
  );

  return (
    <FormDialog
      open
      titleKey="int:compliance.edit"
      screenId={SCR_CONSOLE_021}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      {select('status', 'int:compliance.columns.status', STATUSES, 'int:compliance.status')}
      {text('docsVersion', 'int:compliance.columns.docs')}
      {select('testEnvironmentStatus', 'int:compliance.columns.test_env', ACCESS, 'int:compliance.access')}
      {select('licenseStatus', 'int:compliance.columns.license', ACCESS, 'int:compliance.access')}
      <JalaliDatePickerRHF name="lastApiChangeOn" control={control} labelKey="int:compliance.columns.changed" />
      {text('owner', 'int:compliance.columns.owner')}
      {text('risks', 'int:compliance.risks', true)}
    </FormDialog>
  );
}
